/**
 * @file parts.ts
 * @description Части «вида сайта» и права на них (phase19.16 Р1; SB50 —
 * одна публикация на всё; SB55 — оформление, бренд, SEO — право настроек
 * магазина, меню и блог — право контента; SB60 — «Опубликовать» доступно
 * тому, у кого есть права на все изменённые части; db-design-r1 §3).
 *
 * Соответствие «часть → право» одно для сервера (guard публикации, ответ
 * 403 со списком чужих правок) и кабинета (какие вкладки редактируемы,
 * активна ли кнопка «Опубликовать»). Новое право не заводится (SB55).
 *
 * Коды прав совпадают с enum `Permission` (apps/api/prisma/schema.prisma,
 * каталог ролей apps/api/src/rbac/roles.catalog.ts). Пакет shared не зависит
 * от Prisma, поэтому здесь строковые литералы; сервер сверяет их тестом.
 */

/** Части в порядке показа («Что изменено», история). Совпадает с enum `SiteViewPart`. */
export const SITE_VIEW_PARTS = [
  "THEME",
  "BRAND",
  "SEO",
  "MENUS",
  "BLOG",
  "CATALOG",
  "PRODUCT_TEMPLATE",
  "COUNTERS",
] as const;

export type SiteViewPart = (typeof SITE_VIEW_PARTS)[number];

/**
 * phase19.16 Р3в (§2а; SB145, SB50, SB53; db-design-seo-settings §3) — части
 * вида сайта, которые правятся в черновике и публикуются: всё, кроме SEO.
 * Общие SEO-настройки живут в «Сайт → SEO» и действуют сразу; часть SEO в
 * версиях остаётся зеркалом (по `SITE_VIEW_PARTS` читаются старые версии —
 * сам список не меняется). Используется в diff и `changedParts` публикации,
 * «Отменить изменения», возврате версии.
 */
export const SITE_VIEW_EDITABLE_PARTS: readonly Exclude<SiteViewPart, "SEO">[] = SITE_VIEW_PARTS.filter(
  (part): part is Exclude<SiteViewPart, "SEO"> => part !== "SEO",
);

/** Права, участвующие в виде сайта. */
export type SiteViewPermission =
  | "SHOP_MANAGE_SETTINGS"
  | "SHOP_MANAGE_CONTENT"
  | "SHOP_PUBLISH_CONTENT";

export interface SiteViewPartRights {
  /** Правка части в черновике, «Отменить изменения» по части. */
  readonly edit: SiteViewPermission;
  /** Публикация черновика, в котором изменена часть. */
  readonly publish: SiteViewPermission;
}

/**
 * db-design-r1 §3. Р2а (db-design-r2a, ответы 1 и 2; SB62): CATALOG — вид
 * каталога и карточки, PRODUCT_TEMPLATE — шаблон страницы товара; оба как
 * MENUS (правка — контент, публикация — публикация контента). LAYOUT в Р2а не
 * заводится (места публикуются сразу). Р3г (db-design-r3g §1, §9; SB133):
 * COUNTERS — счётчики и правила согласия посетителя, как THEME (правка и
 * публикация — настройки магазина, SB55). TEXTS — позже.
 */
export const SITE_VIEW_PART_RIGHTS: Readonly<Record<SiteViewPart, SiteViewPartRights>> = {
  THEME: { edit: "SHOP_MANAGE_SETTINGS", publish: "SHOP_MANAGE_SETTINGS" },
  BRAND: { edit: "SHOP_MANAGE_SETTINGS", publish: "SHOP_MANAGE_SETTINGS" },
  SEO: { edit: "SHOP_MANAGE_SETTINGS", publish: "SHOP_MANAGE_SETTINGS" },
  MENUS: { edit: "SHOP_MANAGE_CONTENT", publish: "SHOP_PUBLISH_CONTENT" },
  BLOG: { edit: "SHOP_MANAGE_CONTENT", publish: "SHOP_PUBLISH_CONTENT" },
  CATALOG: { edit: "SHOP_MANAGE_CONTENT", publish: "SHOP_PUBLISH_CONTENT" },
  PRODUCT_TEMPLATE: { edit: "SHOP_MANAGE_CONTENT", publish: "SHOP_PUBLISH_CONTENT" },
  COUNTERS: { edit: "SHOP_MANAGE_SETTINGS", publish: "SHOP_MANAGE_SETTINGS" },
};

/** Названия частей для людей: «Не опубликовано: оформление 3, меню 1». */
export const SITE_VIEW_PART_LABELS: Readonly<Record<SiteViewPart, string>> = {
  THEME: "оформление",
  BRAND: "бренд и контакты",
  SEO: "SEO",
  MENUS: "меню",
  BLOG: "лента блога",
  CATALOG: "каталог",
  PRODUCT_TEMPLATE: "шаблон товара",
  COUNTERS: "счётчики и cookie",
};

/** Описание права для объяснения «опубликовать может сотрудник с …». */
export const SITE_VIEW_PERMISSION_LABELS: Readonly<Record<SiteViewPermission, string>> = {
  SHOP_MANAGE_SETTINGS: "правом настроек магазина",
  SHOP_MANAGE_CONTENT: "правом контента",
  SHOP_PUBLISH_CONTENT: "правом публикации контента",
};

/** Сужение строки до части (параметр маршрута `:part`). */
export function isSiteViewPart(value: string): value is SiteViewPart {
  return (SITE_VIEW_PARTS as readonly string[]).includes(value);
}

/** Права, нужные для публикации набора изменённых частей (без повторов, в порядке частей). */
export function requiredPublishRights(changedParts: Iterable<SiteViewPart>): SiteViewPermission[] {
  const changed = new Set(changedParts);
  const result: SiteViewPermission[] = [];
  for (const part of SITE_VIEW_PARTS) {
    if (!changed.has(part)) continue;
    const right = SITE_VIEW_PART_RIGHTS[part].publish;
    if (!result.includes(right)) result.push(right);
  }
  return result;
}

/**
 * Изменённые части, которые пользователь опубликовать не может (SB60).
 * Пусто — публиковать можно; иначе сервер отвечает 403 со списком
 * `{ part, editedBy, editedAt }` по этим частям.
 */
export function partsBlockingPublish(
  changedParts: Iterable<SiteViewPart>,
  granted: Iterable<string>,
): SiteViewPart[] {
  const has = new Set(granted);
  const changed = new Set(changedParts);
  return SITE_VIEW_PARTS.filter(
    (part) => changed.has(part) && !has.has(SITE_VIEW_PART_RIGHTS[part].publish),
  );
}

/** Части, которые пользователь может править. */
export function editableSiteViewParts(granted: Iterable<string>): SiteViewPart[] {
  const has = new Set(granted);
  return SITE_VIEW_PARTS.filter((part) => has.has(SITE_VIEW_PART_RIGHTS[part].edit));
}

/**
 * Видеть черновик и предпросмотр может любой с правом правки хотя бы одной
 * части (db-design-r1 §3).
 */
export function canViewSiteDraft(granted: Iterable<string>): boolean {
  return editableSiteViewParts(granted).length > 0;
}
